// CONTENT_HASH identifies the exact content a save or replay was made with (docs/ARCHITECTURE.md §11).
// It covers every balance table and the geo dataset (cities, World network candidates and ocean crossings, land mask,
// sea passages, harbours) plus the raw land-navigation document and its shared format version;
// loaders reject a mismatch.
// Not a security hash.

import citiesJson from '../data/cities.json'
import harboursJson from '../data/harbours.json'
import landmaskJson from '../data/landmask.json'
import landNavigationJson from '../data/land-navigation.json'
import worldNetworkJson from '../data/world-network.json'
import * as keys from './balance/keys'
import * as tables from './balance/tables'
import { NAVIGATION_FORMAT_VERSION } from './geometry/navigation-format'
import { OCEAN_CROSSINGS } from './ocean-crossings'
import { SEA_PASSAGES } from './sea-passages'

/** JSON with object keys sorted at every level, so the hash never depends on declaration order. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson((value as Record<string, unknown>)[k])}`)
    return `{${entries.join(',')}}`
  }
  return JSON.stringify(value)
}

/** cyrb53: fast 53-bit string hash, rendered as 14 hex digits. */
function cyrb53(text: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  const value = 4294967296 * (2097151 & h2) + (h1 >>> 0)
  return value.toString(16).padStart(14, '0')
}

const dataOnly = (module: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(module).filter(([, v]) => typeof v !== 'function'))

export const CONTENT_HASH: string = cyrb53(
  canonicalJson({
    keys: dataOnly(keys),
    tables: dataOnly(tables),
    cities: citiesJson,
    worldNetwork: worldNetworkJson,
    crossings: OCEAN_CROSSINGS,
    landmask: landmaskJson,
    passages: SEA_PASSAGES,
    harbours: harboursJson,
    landNavigation: landNavigationJson,
    navigationFormat: { version: NAVIGATION_FORMAT_VERSION },
  }),
)
